import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import type { StoreCsvExport } from '@/client/types/stores/parity';
import { useClientStore } from '@/client/stores/client';
import { getApiErrorMessage } from '@/client/utils/errors';
import { unwrap } from '@/client/api/stores/stores.query';

const EXPORT_PATH = { orders: '/orders/export', transactions: '/transactions/export' } as const;

/** Save a CSV string the API returned as a file download. */
export const downloadCsv = (exp: Pick<StoreCsvExport, 'csv' | 'filename'>) => {
  const url = URL.createObjectURL(new Blob([exp.csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = exp.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * "Export CSV" for a store list. The API returns the CSV inside JSON (both
 * proxies parse bodies as JSON), so the browser builds the file here.
 */
export const ExportButton: React.FC<{
  storeid: number;
  resource: 'orders' | 'transactions';
  params?: Record<string, string | undefined>;
}> = ({ storeid, resource, params = {} }) => {
  const request = useClientStore((s) => s.request);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString();
      const raw = await request(`/api/admin/stores/${storeid}${EXPORT_PATH[resource]}${qs ? `?${qs}` : ''}`, 'GET', undefined, { timeout: 60_000 });
      const exp = unwrap<StoreCsvExport | null>(raw, null);
      if (!exp?.csv) throw new Error('The export came back empty.');
      downloadCsv(exp);
      toast.success(`Exported ${exp.row_count.toLocaleString()} ${resource}`);
      if (exp.truncated) toast.warning('The export hit the 10,000-row limit — narrow the date range for the rest.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Export failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
      Export CSV
    </button>
  );
};
